<script async setup>
import { onMounted, onUnmounted, defineProps, ref, nextTick } from "vue";
import { tns } from "tiny-slider";

const props = defineProps({
  currentSlide: {
    type: Number,
    required: true,
  },
  elementId: {
    type: [Number, String],
    required: true,
  },
  loadMoreLabel: {
    type: String,
    default: "",
  },
});

const currentIndex = ref(0);
const slideCount = ref(1);
const isLargeScreen = ref(false);
const visibleCount = ref(4);
const allItems = ref([]);
let sliderInstance = null;
let lastIsLargeScreen = null;
const sliderId = `slider-${props.elementId}`;

function updateDash(info = sliderInstance?.getInfo?.()) {
  if (!info) return;
  currentIndex.value = Math.min(info.index, slideCount.value - 1);
}

function matchSlideHeights() {
  const items = document.querySelectorAll(`#${sliderId} .tns-item`);
  let maxHeight = 0;
  items.forEach((item) => {
    const child = item.querySelector("*");
    if (!child) return;
    child.style.height = "auto";
    maxHeight = Math.max(maxHeight, child.offsetHeight);
  });
  items.forEach((item) => {
    const child = item.querySelector("*");
    if (child) child.style.height = `${maxHeight}px`;
  });
}

function initSlider() {
  const container = document.querySelector(`#${sliderId}`);
  if (!container) return;

  sliderInstance = tns({
    container: `#${sliderId}`,
    fixedWidth: 240,
    gutter: 40,
    items: 1,
    startIndex: window.elementorEditor ? props.currentSlide - 1 : 0,
    autoplay: false,
    loop: false,
    mouseDrag: true,
    controls: false,
    nav: false,
    edgePadding: 0,
    onInit(info) {
      slideCount.value = Math.max(1, info.slideCount - info.items + 1);
      updateDash(info);
      requestAnimationFrame(matchSlideHeights);
    },
  });

  sliderInstance.events.on("indexChanged", () => {
    updateDash();
    matchSlideHeights();
  });
}

function destroySlider() {
  sliderInstance?.destroy?.();
  sliderInstance = null;
}

function updateVisibleCards() {
  allItems.value.forEach((el, i) => {
    el.classList.toggle("hidden", i >= visibleCount.value);
  });
}

function loadMore() {
  if (!isLargeScreen.value) return;

  const previous = visibleCount.value;
  visibleCount.value += 4;

  allItems.value.forEach((el, i) => {
    if (i >= previous && i < visibleCount.value) {
      el.classList.remove("hidden");
      el.classList.add("opacity-0", "translate-y-4");

      requestAnimationFrame(() => {
        el.classList.add("transition", "duration-500", "ease-out");
        el.classList.remove("opacity-0", "translate-y-4");
      });
    }
  });
}

function handleResize() {
  const matches = window.matchMedia("(min-width: 1040px)").matches;
  if (matches !== lastIsLargeScreen) {
    isLargeScreen.value = matches;
    lastIsLargeScreen = matches;

    if (matches) {
      destroySlider();
      nextTick(() => {
        const cards = document.querySelectorAll(`#social-feed .post-card`);
        allItems.value = Array.from(cards);
        visibleCount.value = 4;
        updateVisibleCards();
      });
    } else {
      nextTick(() => {
        initSlider();
      });
    }
  }
}

onMounted(() => {
  handleResize();
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  destroySlider();
});
</script>

<template>
    <!-- Desktop Grid + Load More -->
    <div v-if="isLargeScreen">
        <div id="social-feed" class="grid grid-cols-4 gap-6 mb-6">
            <slot />
        </div>
  
        <div v-if="visibleCount < allItems.length" class="flex justify-center mb-6">
            <kd-button @click="loadMore" icon="arrow-right" variant="hover-dark">
                {{ loadMoreLabel }}
            </kd-button>
        </div>
    </div>
  
    <!-- MobileSlider -->
    <div v-else class="overflow-hidden">
        <div :id="sliderId">
            <slot />
        </div>
  
        <div
            v-if="slideCount > 1"
            id="track"
            class="w-full bg-ts-900 h-2.5 rounded-full mt-4 relative overflow-hidden border border-white"
        >
            <div
                id="dash"
                class="absolute top-0 h-2 border border-ts-900 bg-white rounded-full transition-all duration-500 ease-in-out"
                :style="{
                    width: `${100 / slideCount}%`,
                    left: `${(100 / slideCount) * currentIndex}%`,
                }"
            />
        </div>
    </div>
</template>
